import { useState } from "react";
import { TimeRange } from "components/charts/TimeRangeNavigator";
import DateRangePicker from "./DateRangePicker";
import {
  TimeNavContainer,
  NavigationButton,
  TimeNavigationSection,
  TimeControlsContainer,
  NowButton,
  PeriodLabel,
  FiltersRow,
  TimeRangeButtons,
  TimeButton,
  ViewControls,
  ViewButton,
} from "styles/devices/deviceChartsNavigation.styles";

export type ViewType = "grid" | "list" | "combined";

interface TimeNavigationProps {
  timeRange: TimeRange;
  timeRanges: TimeRange[];
  navigationOffset: number;
  viewType: ViewType;
  periodLabel: string;
  onTimeRangeChange: (range: TimeRange) => void;
  onNavigateBackward: () => void;
  onNavigateForward: () => void;
  onReturnToNow: () => void;
  onViewTypeChange: (viewType: ViewType) => void;
  onCustomDateRange?: (fromDate: Date, toDate: Date) => void;
  getCurrentDates?: () => { fromDate: Date; toDate: Date };
  isAtCurrentTime?: () => boolean;
  /** When true, NOW is enabled (user panned/zoomed the chart). */
  canReturnToNow?: boolean;
}

export function TimeNavigation({
  timeRange,
  timeRanges,
  navigationOffset,
  viewType,
  periodLabel,
  onTimeRangeChange,
  onNavigateBackward,
  onNavigateForward,
  onReturnToNow,
  onViewTypeChange,
  onCustomDateRange,
  getCurrentDates,
  isAtCurrentTime,
  canReturnToNow = false,
}: TimeNavigationProps) {
  const [showDatePicker, setShowDatePicker] = useState(false);

  const handleCustomDateRangeApply = (fromDate: Date, toDate: Date) => {
    if (onCustomDateRange) onCustomDateRange(fromDate, toDate);
    setShowDatePicker(false);
  };

  const currentDates = getCurrentDates ? getCurrentDates() : undefined;
  const atCurrentTime = isAtCurrentTime
    ? isAtCurrentTime()
    : navigationOffset === 0;
  const isAllTime = timeRange.value === "all";
  const nowEnabled = canReturnToNow && !isAllTime;

  return (
    <TimeNavContainer>
      <NavigationButton
        onClick={onNavigateBackward}
        title="Previous period"
        aria-label="Previous period"
        disabled={isAllTime}
      >
        ←
      </NavigationButton>

      <TimeNavigationSection>
        <PeriodLabel>{periodLabel}</PeriodLabel>
        <FiltersRow>
          <TimeRangeButtons role="group" aria-label="Time range presets">
            {timeRanges.map((range) => (
              <TimeButton
                key={range.label}
                $active={timeRange.label === range.label}
                onClick={() => onTimeRangeChange(range)}
                aria-pressed={timeRange.label === range.label}
                aria-label={`Show last ${range.label}`}
              >
                {range.label}
              </TimeButton>
            ))}
          </TimeRangeButtons>

          <ViewControls role="group" aria-label="Chart view options">
            <ViewButton
              $active={showDatePicker || timeRange.value === "custom"}
              onClick={() => setShowDatePicker(true)}
              title="Select Custom Date Range"
              aria-label="Select custom date range"
            >
              📅
            </ViewButton>
            <ViewButton
              $active={viewType === "list"}
              onClick={() => onViewTypeChange("list")}
              title="List View"
              aria-label="List view"
              aria-pressed={viewType === "list"}
            >
              ☰
            </ViewButton>
            <ViewButton
              $active={viewType === "grid"}
              onClick={() => onViewTypeChange("grid")}
              title="Grid View"
              aria-label="Grid view"
              aria-pressed={viewType === "grid"}
            >
              ▦
            </ViewButton>
            <ViewButton
              $active={viewType === "combined"}
              onClick={() => onViewTypeChange("combined")}
              title="Combined View"
              aria-label="Combined view"
              aria-pressed={viewType === "combined"}
            >
              ⧉
            </ViewButton>
          </ViewControls>
        </FiltersRow>
      </TimeNavigationSection>

      <TimeControlsContainer>
        <NavigationButton
          onClick={onNavigateForward}
          disabled={atCurrentTime || isAllTime}
          title="Next period"
          aria-label="Next period"
        >
          →
        </NavigationButton>
        <NowButton
          type="button"
          onClick={onReturnToNow}
          disabled={!nowEnabled}
          title={
            nowEnabled
              ? "Return to current time"
              : "Pan or zoom the chart to enable NOW"
          }
          aria-label="Return to current time"
          aria-disabled={!nowEnabled}
        >
          NOW
        </NowButton>
      </TimeControlsContainer>

      <DateRangePicker
        visible={showDatePicker}
        onHide={() => setShowDatePicker(false)}
        onApply={handleCustomDateRangeApply}
        initialFromDate={currentDates?.fromDate}
        initialToDate={currentDates?.toDate}
      />
    </TimeNavContainer>
  );
}
